import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Card,
  Badge,
  Alert,
  Table,
  InputGroup
} from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import {
  FaBolt,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaEyeSlash,
  FaCheck,
  FaTimes,
  FaSearch,
  FaFilter
} from 'react-icons/fa';
import axios from 'axios';
import DataApi from '../../api/DataApi';
import { crmAuthHeaders } from '../../api/DataApi';
import { getCustomScriptByIdOrKey } from '../../api/CustomScriptApi';
import { API_BASE_URL } from '../../constants/CONSTANT';
import { filterObjectManagerAllowedObjects } from '../../utils/filterObjectManagerAllowedObjects';
import { getEntitlementsSection } from '../../utils/entitlements';
import { fetchUiComponentRegistryRows } from '../layout/ComponentRegistry';
import AppToast from '../AppToast';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import './css/ActionManager.css';

/** Stored action_type → list/form label (same as Form.Select options). */
const ACTION_TYPE_LABELS = {
  row_action: 'Row Action',
  list_action: 'List Action',
  detail_action: 'Detail Action',
  detail_actions: 'Detail Action',
  edit_action: 'Edit Action',
  bulk_action: 'Bulk Action',
  global_action: 'Global Action',
  // legacy / alternate keys
  row: 'Row Action',
  bulk: 'Bulk Action',
  page: 'Page Action',
  global: 'Global Action',
};

/** Action Manager only creates/lists detail-page actions (matches DetailLayout custom actions). */
const ACTION_MANAGER_ACTION_TYPE = 'detail_action';

function isDetailActionType(actionType) {
  const k = String(actionType || '').trim().toLowerCase();
  return k === 'detail_action' || k === 'detail_actions';
}

/** Stored action_behavior → label (includes legacy values still shown in list / legacy edit option). */
const ACTION_BEHAVIOR_LABELS = {
  new: 'New',
  edit: 'Edit',
  delete: 'Delete',
  import: 'Import',
  export: 'Export',
  url: 'URL',
  component: 'Component',
  custom_script: 'Custom Script',
  /** Detail dynamic form: custom_script.script_type = view_page */
  view_page: 'View Page',
};

/** Action Manager UI: selectable behaviors for new/edit (legacy values remain in DB until migrated). */
function isSelectableActionBehavior(value) {
  const k = String(value || '').trim().toLowerCase();
  return (
    k === 'url' ||
    k === 'component' ||
    k === 'custom_script' ||
    k === 'view_page'
  );
}

function labelForActionType(type) {
  const k = String(type || '').trim().toLowerCase();
  if (!k) return '—';
  return ACTION_TYPE_LABELS[k] || type;
}

function labelForActionBehavior(behavior) {
  const k = String(behavior || '').trim().toLowerCase();
  if (!k) return '—';
  if (k === 'web_component' || k === 'ui_schema' || k === 'view_page') {
    return ACTION_BEHAVIOR_LABELS.view_page;
  }
  return ACTION_BEHAVIOR_LABELS[k] || behavior;
}

/** Registry propSpec.type: field = object field dropdown; text = static string. */
function propSpecSourceType(spec) {
  const t = spec?.type;
  if (t === 'text') return 'text';
  return 'field';
}

/** Form draft string from stored propMappings entry (legacy string or { sourceType, value }). */
function propMappingEntryToDraftValue(entry) {
  if (entry == null || entry === '') return '';
  if (typeof entry === 'string') return entry;
  if (typeof entry === 'object' && entry.value != null) return String(entry.value);
  return '';
}

function propMappingsToDraft(propMappings) {
  if (!propMappings || typeof propMappings !== 'object' || Array.isArray(propMappings)) {
    return {};
  }
  const draft = {};
  Object.entries(propMappings).forEach(([k, v]) => {
    draft[k] = propMappingEntryToDraftValue(v);
  });
  return draft;
}

/** Build stored propMappings from propSpec + draft (same shape as component actions). */
function buildPropMappingsFromSpecs(propSpecs, propMappingDraft) {
  const pmFromRows = {};
  if (!Array.isArray(propSpecs) || propSpecs.length === 0) return pmFromRows;
  for (const spec of propSpecs) {
    const p = spec?.prop;
    if (!p) continue;
    const raw = String(propMappingDraft[p] || '').trim();
    if (!raw) continue;
    if (propSpecSourceType(spec) === 'text') {
      pmFromRows[p] = { sourceType: 'text', value: raw };
    } else {
      pmFromRows[p] = { sourceType: 'field', value: raw };
    }
  }
  return pmFromRows;
}

function normalizeViewPagePropSpecEntry(raw) {
  const prop = String(raw?.prop ?? raw?.name ?? raw?.fieldName ?? '').trim();
  if (!prop) return null;
  const label = raw?.label != null ? String(raw.label) : prop;
  const description = raw?.description != null ? String(raw.description) : '';
  const type = raw?.type === 'text' ? 'text' : 'field';
  const entry = { prop, label, type };
  if (description) entry.description = description;
  return entry;
}

/** Parse ui_code when the API returns a JSON string (defensive). */
function parseUiCodeMaybe(scriptDetail) {
  const raw = scriptDetail?.ui_code;
  if (raw == null) return null;
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return p && typeof p === 'object' && !Array.isArray(p) ? p : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Parameter list for Action Manager when linking a view_page script:
 * use propSpec from ui_code or code (resolved form JSON), then form fields.
 */
function buildViewPagePropSpecs(scriptDetail) {
  if (!scriptDetail || typeof scriptDetail !== 'object') return [];
  const uiCode = parseUiCodeMaybe(scriptDetail);
  const code =
    scriptDetail.code && typeof scriptDetail.code === 'object' && !Array.isArray(scriptDetail.code)
      ? scriptDetail.code
      : null;

  const propSpecsFrom = (form) =>
    form && Array.isArray(form.propSpec) && form.propSpec.length > 0 ? form.propSpec : null;

  const fromPropSpec = propSpecsFrom(uiCode) || propSpecsFrom(code);
  if (fromPropSpec) {
    return fromPropSpec.map(normalizeViewPagePropSpecEntry).filter(Boolean);
  }

  const rawFieldsSource = code || uiCode;
  const rawFields =
    rawFieldsSource && Array.isArray(rawFieldsSource.fields) ? rawFieldsSource.fields : [];
  return rawFields
    .map((f) =>
      normalizeViewPagePropSpecEntry({
        prop: f?.name ?? f?.fieldName,
        label: f?.label,
        description: f?.description,
        type: 'field',
      })
    )
    .filter(Boolean);
}

/** Active custom_script rows for Action Manager; default object-scoped only (matchObjectOnly). */
async function fetchCustomScriptsForPicker(signal, targetObject, options = {}) {
  const matchObjectOnly = options.matchObjectOnly !== false;
  const onlyScriptTypes = options.onlyScriptTypes;
  const params = new URLSearchParams();
  if (targetObject != null && String(targetObject).trim() !== '') {
    params.set('targetObject', String(targetObject).trim());
  }
  if (matchObjectOnly) {
    params.set('matchObjectOnly', 'true');
  }
  const qs = params.toString();
  const url = `${API_BASE_URL}/api/custom-scripts${qs ? `?${qs}` : ''}`;
  const res = await axios.get(url, {
    headers: { ...crmAuthHeaders() },
    signal,
  });
  if (!res.data?.success || !Array.isArray(res.data.data)) {
    return [];
  }
  let rows = res.data.data;
  if (Array.isArray(onlyScriptTypes) && onlyScriptTypes.length > 0) {
    const allow = new Set(onlyScriptTypes.map((t) => String(t).toLowerCase()));
    rows = rows.filter((r) => allow.has(String(r.script_type || '').toLowerCase()));
  }
  return rows;
}

/** Same as FieldManager: company_subscription / license objectManager.enable_standard ("Enable Standard"). */
const isEntitlementEnabled = (value) =>
  value === true || value === 'true' || value === 1 || value === '1';

const ActionManager = ({ show, onHide, objectName: propObjectName }) => {
  const { objectName: urlObjectName } = useParams();
  const objectName = propObjectName || urlObjectName;
  /** Object context from Object Manager or `/setup/:objectName/actions` — target_object must stay aligned. */
  const targetObjectContextLocked = Boolean(String(objectName || '').trim());
  const objectManagerConfig = getEntitlementsSection('objectManager', {});
  const allowStandardActionCheckbox =
    isEntitlementEnabled(objectManagerConfig?.enable_standard) ||
    isEntitlementEnabled(objectManagerConfig?.enableStandard);
  /** When Enable Standard is on, standard actions are not editable (same idea as protecting seeded config). */
  const isStandardActionRowLocked = (action) =>
    allowStandardActionCheckbox && !!action?.is_standard;

  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [actionToDelete, setActionToDelete] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showActionModal, setShowActionModal] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [objects, setObjects] = useState([]);
  const [registryRows, setRegistryRows] = useState([]);
  const [customScriptRows, setCustomScriptRows] = useState([]);
  const [webComponentScriptRows, setWebComponentScriptRows] = useState([]);
  /** Loaded custom_script row (GET by id) for view_page parameter mapping. */
  const [viewPageScriptDetail, setViewPageScriptDetail] = useState(null);
  const [viewPageScriptDetailLoading, setViewPageScriptDetailLoading] = useState(false);
  const [targetObjectFields, setTargetObjectFields] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [actionForm, setActionForm] = useState({
    action_label: '',
    action_name: '',
    action_type: ACTION_MANAGER_ACTION_TYPE,
    target_url: '',
    component_name: '',
    is_active: true,
    is_standard: false,
    action_behavior: '',
    target_object: '',
    prop_mapping_draft: {},
    custom_script_id: '',
    view_page_script_id: ''
  });

  useEffect(() => {
    if (show) {
      fetchActions();
      fetchObjects();
    }
  }, [show, objectName]);

  useEffect(() => {
    if (!show || !targetObjectContextLocked) return;
    const next = String(objectName).trim();
    setActionForm((prev) => (prev.target_object === next ? prev : { ...prev, target_object: next }));
  }, [show, targetObjectContextLocked, objectName]);

  useEffect(() => {
    if (!showActionModal || actionForm.action_behavior !== 'component') {
      return undefined;
    }
    const ac = new AbortController();
    const target = (actionForm.target_object || '').trim();
    (async () => {
      try {
        const rows = await fetchUiComponentRegistryRows(ac.signal, target || null, {
          matchObjectOnly: true,
        });
        if (!ac.signal.aborted) {
          setRegistryRows(rows);
        }
      } catch {
        if (!ac.signal.aborted) {
          setRegistryRows([]);
        }
      }
    })();
    return () => ac.abort();
  }, [showActionModal, actionForm.action_behavior, actionForm.target_object]);

  useEffect(() => {
    if (!showActionModal || actionForm.action_behavior !== 'custom_script') {
      return undefined;
    }
    const ac = new AbortController();
    const target = (actionForm.target_object || '').trim();
    (async () => {
      try {
        const rows = await fetchCustomScriptsForPicker(ac.signal, target || null, {
          matchObjectOnly: true,
        });
        if (!ac.signal.aborted) {
          setCustomScriptRows(rows);
        }
      } catch {
        if (!ac.signal.aborted) {
          setCustomScriptRows([]);
        }
      }
    })();
    return () => ac.abort();
  }, [showActionModal, actionForm.action_behavior, actionForm.target_object]);

  useEffect(() => {
    if (!showActionModal || actionForm.action_behavior !== 'view_page') {
      return undefined;
    }
    const ac = new AbortController();
    const target = (actionForm.target_object || '').trim();
    (async () => {
      try {
        const rows = await fetchCustomScriptsForPicker(ac.signal, target || null, {
          matchObjectOnly: false,
          onlyScriptTypes: ['view_page', 'web_component', 'ui_schema'],
        });
        if (!ac.signal.aborted) {
          setWebComponentScriptRows(rows);
        }
      } catch {
        if (!ac.signal.aborted) {
          setWebComponentScriptRows([]);
        }
      }
    })();
    return () => ac.abort();
  }, [showActionModal, actionForm.action_behavior, actionForm.target_object]);

  useEffect(() => {
    if (!showActionModal) {
      setViewPageScriptDetail(null);
      setViewPageScriptDetailLoading(false);
      return undefined;
    }
    if (actionForm.action_behavior !== 'view_page') {
      setViewPageScriptDetail(null);
      setViewPageScriptDetailLoading(false);
      return undefined;
    }
    const sid = String(actionForm.view_page_script_id || '').trim();
    if (!sid) {
      setViewPageScriptDetail(null);
      setViewPageScriptDetailLoading(false);
      return undefined;
    }
    let cancelled = false;
    setViewPageScriptDetailLoading(true);
    setViewPageScriptDetail(null);
    (async () => {
      try {
        const res = await getCustomScriptByIdOrKey(sid);
        if (cancelled) return;
        if (!res?.success) {
          setViewPageScriptDetail(null);
        } else {
          setViewPageScriptDetail(res.data);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('ActionManager: view page script detail', e);
          setViewPageScriptDetail(null);
        }
      } finally {
        if (!cancelled) {
          setViewPageScriptDetailLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showActionModal, actionForm.action_behavior, actionForm.view_page_script_id]);

  const selectedRegistryRow = useMemo(
    () => registryRows.find((r) => r.component_key === actionForm.component_name),
    [registryRows, actionForm.component_name]
  );

  const propSpecNeedsTargetFields = useMemo(() => {
    const specs = selectedRegistryRow?.config?.propSpec;
    if (!Array.isArray(specs)) return false;
    return specs.some((s) => propSpecSourceType(s) === 'field');
  }, [selectedRegistryRow?.config?.propSpec]);

  const viewPagePropSpecs = useMemo(
    () => buildViewPagePropSpecs(viewPageScriptDetail),
    [viewPageScriptDetail]
  );

  const viewPagePropSpecNeedsTargetFields = useMemo(() => {
    if (!Array.isArray(viewPagePropSpecs) || viewPagePropSpecs.length === 0) return false;
    return viewPagePropSpecs.some((s) => propSpecSourceType(s) === 'field');
  }, [viewPagePropSpecs]);

  useEffect(() => {
    if (!showActionModal) {
      setTargetObjectFields([]);
      return undefined;
    }
    const loadForComponent =
      actionForm.action_behavior === 'component' && propSpecNeedsTargetFields;
    const loadForViewPage =
      actionForm.action_behavior === 'view_page' && viewPagePropSpecNeedsTargetFields;
    if (!loadForComponent && !loadForViewPage) {
      setTargetObjectFields([]);
      return undefined;
    }
    const to = (actionForm.target_object || '').trim();
    if (!to) {
      setTargetObjectFields([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const api = new DataApi(`objectconfig/${to}`);
        const response = await api.fetchAll();
        if (cancelled) return;
        const data = response?.data?.data;
        const fields = data?.fields && Array.isArray(data.fields) ? data.fields : [];
        setTargetObjectFields(fields);
      } catch (e) {
        if (!cancelled) {
          console.error('ActionManager: object fields', e);
          setTargetObjectFields([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    showActionModal,
    actionForm.action_behavior,
    actionForm.target_object,
    propSpecNeedsTargetFields,
    viewPagePropSpecNeedsTargetFields,
  ]);

  const targetObjectFieldOptions = useMemo(() => {
    const to = (actionForm.target_object || '').trim();
    if (!to || !Array.isArray(targetObjectFields) || targetObjectFields.length === 0) {
      return [];
    }
    return targetObjectFields
      .filter((f) => f.field_name && !f.is_deleted)
      .map((f) => ({
        value: `${to}.${f.field_name}`,
        label: `${f.field_label || f.field_name} (${to}.${f.field_name})`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [actionForm.target_object, targetObjectFields]);

  /** Normalize legacy propMappings values like "id" to "object.id" when options load. */
  useEffect(() => {
    const to = (actionForm.target_object || '').trim();
    if (!to || targetObjectFieldOptions.length === 0) return;
    const propSpecs =
      actionForm.action_behavior === 'component'
        ? selectedRegistryRow?.config?.propSpec
        : actionForm.action_behavior === 'view_page'
          ? viewPagePropSpecs
          : null;
    if (!Array.isArray(propSpecs) || propSpecs.length === 0) return;
    setActionForm((prev) => {
      let changed = false;
      const nextDraft = { ...prev.prop_mapping_draft };
      for (const spec of propSpecs) {
        if (propSpecSourceType(spec) !== 'field') continue;
        const p = spec?.prop;
        if (!p) continue;
        let v = nextDraft[p];
        if (v == null || v === '') continue;
        v = String(v);
        if (!v.includes('.')) {
          const dotted = `${to}.${v}`;
          if (targetObjectFieldOptions.some((o) => o.value === dotted)) {
            nextDraft[p] = dotted;
            changed = true;
          }
        }
      }
      return changed ? { ...prev, prop_mapping_draft: nextDraft } : prev;
    });
  }, [
    actionForm.target_object,
    targetObjectFieldOptions,
    actionForm.action_behavior,
    selectedRegistryRow?.config?.propSpec,
    selectedRegistryRow?.component_key,
    viewPagePropSpecs,
    actionForm.view_page_script_id,
  ]);

  const fetchActions = async () => {
    try {
      setLoading(true);
      setError('');

      const api = new DataApi('actions');
      const response = await api.fetchAll();

      if (response?.data?.success) {
        let allActions = response.data.data || [];

        // Filter actions by objectName if provided
        if (objectName) {
          allActions = allActions.filter(action => action.target_object === objectName);
        }

        setActions(allActions);
      } else {
        setToast({ show: true, message: 'Failed to load actions', type: 'error' });
      }
    } catch (error) {
      console.error('Error fetching actions:', error);
      const errorMessage = error.message || 'An unexpected error occurred while loading actions';
      setToast({ show: true, message: errorMessage, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchObjects = async () => {
    try {
      const api = new DataApi('objectconfig');
      const response = await api.fetchAll();

      if (response?.data?.success) {
        const objectsWithFields = response.data.data || [];
        const allowed = filterObjectManagerAllowedObjects(objectsWithFields).filter(
          (obj) => obj.object_name !== 'attachment'
        );
        const activeObjects = allowed
          .map((obj) => ({
            value: obj.object_name,
            label: obj.object_label || obj.object_name,
          }))
          .sort((a, b) => {
            const byLabel = String(a.label).localeCompare(String(b.label), undefined, {
              sensitivity: 'base',
            });
            if (byLabel !== 0) return byLabel;
            return String(a.value).localeCompare(String(b.value), undefined, {
              sensitivity: 'base',
            });
          });
        setObjects(activeObjects);
      }
    } catch (error) {
      console.error('Error fetching objects:', error);
    }
  };


  const generateActionName = (label) => {
    if (!label) return '';

    // Convert to lowercase, replace spaces and special characters with underscores
    return label
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters except spaces and numbers
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/_+/g, '_') // Replace multiple underscores with single underscore
      .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
  };

  const handleActionLabelChange = (value) => {
    const generatedName = generateActionName(value);
    setActionForm({
      ...actionForm,
      action_label: value,
      action_name: generatedName
    });
  };

  const resetForm = () => {
    setEditingAction(null);
    setViewPageScriptDetail(null);
    setViewPageScriptDetailLoading(false);
    setActionForm({
      action_label: '',
      action_name: '',
      action_type: ACTION_MANAGER_ACTION_TYPE,
      target_url: '',
      component_name: '',
      is_active: true,
      is_standard: false,
      action_behavior: '',
      target_object: objectName || '',
      prop_mapping_draft: {},
      custom_script_id: '',
      view_page_script_id: ''
    });
  };

  const handleCreateAction = () => {
    resetForm();
    setShowActionModal(true);
  };

  const handleEditAction = (action) => {
    if (isStandardActionRowLocked(action)) {
      setToast({
        show: true,
        message:
          'Standard actions cannot be edited while Enable Standard is on for your subscription.',
        type: 'warning',
      });
      return;
    }
    setEditingAction(action);
    setActionForm({
      action_label: action.action_label || '',
      action_name: action.action_name || '',
      action_type: ACTION_MANAGER_ACTION_TYPE,
      target_url: action.target_url || '',
      component_name: action.component_name || '',
      is_active: action.is_active !== false,
      is_standard: action.is_standard || false,
      action_behavior: (() => {
        const b = String(action.action_behavior || '').trim().toLowerCase();
        if (b === 'ui_schema' || b === 'web_component' || b === 'view_page') return 'view_page';
        return action.action_behavior || '';
      })(),
      target_object: action.target_object || '',
      prop_mapping_draft: propMappingsToDraft(
        action.config && typeof action.config === 'object' ? action.config.propMappings : null
      ),
      custom_script_id: (() => {
        const c = action.config && typeof action.config === 'object' ? action.config : null;
        if (!c) return '';
        return String(c.customScriptId || c.custom_script_id || '').trim();
      })(),
      view_page_script_id: (() => {
        const c = action.config && typeof action.config === 'object' ? action.config : null;
        if (!c) return '';
        return String(
          c.viewPageScriptId ||
            c.view_page_script_id ||
            c.webComponentScriptId ||
            c.web_component_script_id ||
            c.uiSchemaScriptId ||
            c.ui_schema_script_id ||
            ''
        ).trim();
      })()
    });
    setShowActionModal(true);
  };

  const handleSaveAction = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      setToast({ show: false, message: '', type: 'success' });

      if (editingAction && isStandardActionRowLocked(editingAction)) {
        setToast({
          show: true,
          message:
            'Standard actions cannot be updated while Enable Standard is on for your subscription.',
          type: 'error',
        });
        setLoading(false);
        return;
      }

      // Validation
      if (!actionForm.action_label.trim()) {
        setToast({ show: true, message: 'Action label is required', type: 'error' });
        return;
      }

      // Generate action name if it's empty (shouldn't happen with auto-generation, but as fallback)
      const finalActionName = actionForm.action_name.trim() || generateActionName(actionForm.action_label);
      if (!finalActionName) {
        setToast({ show: true, message: 'Action name could not be generated from label', type: 'error' });
        return;
      }

      const effectiveTargetObject = targetObjectContextLocked
        ? String(objectName).trim()
        : String(actionForm.target_object || '').trim();

      if (actionForm.action_behavior === 'component' && !String(actionForm.component_name || '').trim()) {
        setToast({ show: true, message: 'Component is required', type: 'error' });
        return;
      }

      if (actionForm.action_behavior === 'custom_script') {
        if (!effectiveTargetObject) {
          setToast({
            show: true,
            message: 'Target object is required to attach a custom script.',
            type: 'error',
          });
          return;
        }
        if (!String(actionForm.custom_script_id || '').trim()) {
          setToast({ show: true, message: 'Select a custom script.', type: 'error' });
          return;
        }
      }

      if (actionForm.action_behavior === 'view_page') {
        if (!effectiveTargetObject) {
          setToast({
            show: true,
            message: 'Target object is required for view page actions.',
            type: 'error',
          });
          return;
        }
        if (!String(actionForm.view_page_script_id || '').trim()) {
          setToast({
            show: true,
            message: 'Select a view page script.',
            type: 'error',
          });
          return;
        }
      }

      let parsedConfig = null;

      if (actionForm.action_behavior === 'component') {
        const propSpecs = selectedRegistryRow?.config?.propSpec;
        const hasPropSpecs = Array.isArray(propSpecs) && propSpecs.length > 0;
        let pmFromRows = {};
        if (hasPropSpecs) {
          pmFromRows = buildPropMappingsFromSpecs(propSpecs, actionForm.prop_mapping_draft);
        } else {
          Object.entries(actionForm.prop_mapping_draft || {}).forEach(([k, v]) => {
            const t = String(v || '').trim();
            if (t) pmFromRows[k] = t;
          });
        }
        if (Object.keys(pmFromRows).length > 0) {
          parsedConfig = { propMappings: pmFromRows };
        }
      } else if (actionForm.action_behavior === 'custom_script') {
        const sid = String(actionForm.custom_script_id || '').trim();
        const row = customScriptRows.find((r) => String(r.id) === sid);
        const prevCfg =
          editingAction?.config && typeof editingAction.config === 'object'
            ? editingAction.config
            : null;
        const scriptKey =
          row?.script_key ||
          (prevCfg && String(prevCfg.scriptKey || prevCfg.script_key || '').trim()) ||
          '';
        if (sid && scriptKey) {
          parsedConfig = { customScriptId: sid, scriptKey };
        } else if (sid) {
          setToast({
            show: true,
            message: 'Could not resolve script key. Wait for the list to load or re-select the script.',
            type: 'error',
          });
          setLoading(false);
          return;
        }
      } else if (actionForm.action_behavior === 'view_page') {
        const sid = String(actionForm.view_page_script_id || '').trim();
        const row = webComponentScriptRows.find((r) => String(r.id) === sid);
        const prevCfg =
          editingAction?.config && typeof editingAction.config === 'object'
            ? editingAction.config
            : null;
        const scriptKey =
          row?.script_key ||
          (prevCfg && String(prevCfg.scriptKey || prevCfg.script_key || '').trim()) ||
          '';
        if (sid && scriptKey) {
          const propSpecs = viewPagePropSpecs;
          const hasPropSpecs = Array.isArray(propSpecs) && propSpecs.length > 0;
          let pmFromRows = {};
          if (hasPropSpecs) {
            pmFromRows = buildPropMappingsFromSpecs(propSpecs, actionForm.prop_mapping_draft);
          } else {
            Object.entries(actionForm.prop_mapping_draft || {}).forEach(([k, v]) => {
              const t = String(v || '').trim();
              if (t) pmFromRows[k] = { sourceType: 'field', value: t };
            });
          }
          parsedConfig = { viewPageScriptId: sid, scriptKey };
          if (Object.keys(pmFromRows).length > 0) {
            parsedConfig.propMappings = pmFromRows;
          }
        } else if (sid) {
          setToast({
            show: true,
            message: 'Could not resolve script key. Wait for the list to load or re-select the script.',
            type: 'error',
          });
          setLoading(false);
          return;
        }
      }

      const formData = {
        action_label: actionForm.action_label,
        action_name: finalActionName,
        icon: null,
        action_type: ACTION_MANAGER_ACTION_TYPE,
        target_url: actionForm.action_behavior === 'url' ? actionForm.target_url : '',
        component_name: actionForm.action_behavior === 'component' ? actionForm.component_name : '',
        is_active: actionForm.is_active,
        is_standard: actionForm.is_standard,
        action_behavior: actionForm.action_behavior,
        open_in_modal: false,
        target_object: effectiveTargetObject,
        config: parsedConfig
      };

      const api = new DataApi('actions');
      let response;

      if (editingAction) {
        response = await api.update(formData, editingAction.id);
      } else {
        response = await api.create(formData);
      }

      if (response?.data?.success) {
        const toastMessage = `Action ${editingAction ? 'updated' : 'created'} successfully!`;
        setToast({
          show: true,
          message: toastMessage,
          type: 'success'
        });
        setShowActionModal(false);
        fetchActions();
        resetForm();
      } else {
        const errorMessage = response?.data?.message || 'Failed to save action';
        setToast({
          show: true,
          message: errorMessage,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error saving action:', error);
      const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred while saving the action';
      setToast({ show: true, message: errorMessage, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAction = (action) => {
    if (isStandardActionRowLocked(action)) {
      setToast({
        show: true,
        message:
          'Standard actions cannot be deleted while Enable Standard is on for your subscription.',
        type: 'warning',
      });
      return;
    }
    setActionToDelete(action);
    setShowDeleteModal(true);
  };

  const confirmDeleteAction = async () => {
    if (!actionToDelete) return;

    try {
      setLoading(true);
      setToast({ show: false, message: '', type: 'success' });

      const api = new DataApi('actions');
      const response = await api.delete(actionToDelete.id);

      if (response?.data?.success) {
        setActions(actions.filter(a => a.id !== actionToDelete.id));
        setToast({ show: true, message: 'Action deleted successfully!', type: 'success' });
      } else {
        setToast({ show: true, message: 'Failed to delete action', type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting action:', error);
      const errorMessage = error.message || 'An unexpected error occurred while deleting the action';
      setToast({ show: true, message: errorMessage, type: 'error' });
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setActionToDelete(null);
    }
  };

  const targetObjectLabelByApiName = useMemo(() => {
    const m = new Map();
    objects.forEach((o) => {
      m.set(String(o.value).toLowerCase(), o.label);
    });
    return m;
  }, [objects]);

  const getTargetObjectDisplayLabel = (apiName) => {
    if (apiName == null || String(apiName).trim() === '') return null;
    const key = String(apiName).trim().toLowerCase();
    return targetObjectLabelByApiName.get(key) || apiName;
  };

  const getActionTypeBadge = (type) => {
    const k = String(type || '').trim().toLowerCase();
    const label = labelForActionType(type);
    const variants = {
      row_action: 'primary',
      list_action: 'secondary',
      detail_action: 'info',
      detail_actions: 'info',
      edit_action: 'warning',
      bulk_action: 'success',
      global_action: 'dark',
      row: 'primary',
      bulk: 'success',
      page: 'info',
      global: 'warning',
    };
    return <Badge bg={variants[k] || 'secondary'}>{label}</Badge>;
  };

  // Detail actions only (list + create/edit); other action_type rows are managed elsewhere.
  const filteredActions = actions.filter((action) => {
    if (!isDetailActionType(action.action_type)) return false;
    const matchesSearch =
      action.action_label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      action.action_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (action.component_name &&
        action.component_name.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const renderPropMappingSection = (propSpecs, title) => {
    if (!Array.isArray(propSpecs) || propSpecs.length === 0) return null;
    const needsTargetFieldsForSection = propSpecs.some(
      (spec) => propSpecSourceType(spec) === 'field'
    );
    return (
      <Row className="mt-2">
        <Col md={12}>
          <Form.Label className="fw-semibold">{title}</Form.Label>
          <Form.Text className="text-muted d-block mb-2">
            <strong>Field</strong>: choose a column on the target object (stored as{' '}
            <code>object.field</code> with <code>sourceType: &quot;field&quot;</code>
            ). <strong>Text</strong>: enter a fixed value (<code>sourceType: &quot;text&quot;</code>
            ).
          </Form.Text>
          {needsTargetFieldsForSection && !actionForm.target_object?.trim() && (
            <Alert variant="warning" className="py-2 small">
              Select a <strong>target object</strong> so field-type parameters can use the field
              dropdown.
            </Alert>
          )}
          <Table bordered size="sm" className="mb-3 align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: '34%' }}>Parameter</th>
                <th style={{ width: '12%' }}>Kind</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {propSpecs.map((spec) => {
                const propKey = spec?.prop;
                if (!propKey) return null;
                const kind = propSpecSourceType(spec);
                const currentVal = actionForm.prop_mapping_draft[propKey] || '';
                const hasCurrentExtra =
                  kind === 'field' &&
                  currentVal &&
                  !targetObjectFieldOptions.some((o) => o.value === currentVal);
                return (
                  <tr key={propKey}>
                    <td>
                      <div className="fw-medium">{spec.label || propKey}</div>
                      {spec.description && (
                        <div className="small text-muted">{spec.description}</div>
                      )}
                      <code className="small text-secondary">{propKey}</code>
                    </td>
                    <td>
                      <Badge bg={kind === 'text' ? 'secondary' : 'primary'}>
                        {kind === 'text' ? 'Text' : 'Field'}
                      </Badge>
                    </td>
                    <td>
                      {kind === 'text' ? (
                        <Form.Control
                          type="text"
                          value={currentVal}
                          onChange={(e) =>
                            setActionForm((prev) => ({
                              ...prev,
                              prop_mapping_draft: {
                                ...prev.prop_mapping_draft,
                                [propKey]: e.target.value,
                              },
                            }))
                          }
                          placeholder="Static value"
                        />
                      ) : !actionForm.target_object?.trim() ? (
                        <span className="text-muted small">Select target object first</span>
                      ) : targetObjectFieldOptions.length === 0 ? (
                        <span className="text-muted small">
                          Loading fields for {actionForm.target_object}…
                        </span>
                      ) : (
                        <Form.Select
                          value={currentVal}
                          onChange={(e) =>
                            setActionForm((prev) => ({
                              ...prev,
                              prop_mapping_draft: {
                                ...prev.prop_mapping_draft,
                                [propKey]: e.target.value,
                              },
                            }))
                          }
                        >
                          <option value="">Select field…</option>
                          {hasCurrentExtra && (
                            <option value={currentVal}>
                              {currentVal} (current value)
                            </option>
                          )}
                          {targetObjectFieldOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Form.Select>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Col>
      </Row>
    );
  };

  const renderActionModal = () => (
    <Modal show={showActionModal} onHide={() => setShowActionModal(false)} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {editingAction ? 'Edit Action' : 'Create New Action'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Action Label *</Form.Label>
                <Form.Control
                  type="text"
                  value={actionForm.action_label}
                  onChange={(e) => handleActionLabelChange(e.target.value)}
                  placeholder="e.g., View Record"
                />
                <Form.Text className="text-muted">
                  Display name for the action
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Action Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={actionForm.action_name}
                  readOnly
                  className="bg-light"
                  placeholder="e.g., view_record"
                />
                <Form.Text className="text-muted">
                  <FaCheck className="me-1 text-success" />
                  Auto-generated from action label (lowercase, underscores)
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Action type</Form.Label>
                <Form.Control
                  type="text"
                  readOnly
                  className="bg-light"
                  value={labelForActionType(ACTION_MANAGER_ACTION_TYPE)}
                />
                <Form.Text className="text-muted">
                  Detail actions only — they appear on the record detail page (Action Manager does not
                  create list, bulk, or global actions).
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Target Object</Form.Label>
                {targetObjectContextLocked ? (
                  <Form.Control
                    type="text"
                    readOnly
                    className="bg-light"
                    value={getTargetObjectDisplayLabel(objectName) || objectName || ''}
                  />
                ) : (
                  <Form.Select
                    value={actionForm.target_object}
                    onChange={(e) => {
                      const v = e.target.value;
                      setActionForm((prev) => ({
                        ...prev,
                        target_object: v,
                        ...(prev.action_behavior === 'component'
                          ? { component_name: '', prop_mapping_draft: {} }
                          : {}),
                        ...(prev.action_behavior === 'custom_script' ? { custom_script_id: '' } : {}),
                        ...(prev.action_behavior === 'view_page' ? { view_page_script_id: '' } : {})
                      }));
                    }}
                  >
                    <option value="">Select an object (optional)</option>
                    {objects.map((obj) => (
                      <option key={obj.value} value={obj.value}>
                        {obj.label}
                      </option>
                    ))}
                    {actionForm.target_object &&
                      !objects.some((o) => o.value === actionForm.target_object) && (
                        <option value={actionForm.target_object}>
                          {actionForm.target_object} (current — not in allowlist)
                        </option>
                      )}
                  </Form.Select>
                )}
                <Form.Text className="text-muted">
                  {targetObjectContextLocked
                    ? 'Actions are scoped to this object; target_object is set to match where you opened Action Manager.'
                    : actionForm.action_behavior === 'view_page'
                      ? 'Required for View Page actions — lists custom_script rows with script_type view_page (object + universal).'
                      : 'Only objects allowed for this tenant (same rules as Object Manager) and with explore enabled.'}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Action Behavior</Form.Label>
                <Form.Select
                  value={actionForm.action_behavior}
                  onChange={(e) => {
                    const v = e.target.value;
                    setActionForm((prev) => ({
                      ...prev,
                      action_behavior: v,
                      ...(v !== 'component' ? { prop_mapping_draft: {} } : {}),
                      ...(v !== 'component' ? { component_name: '' } : {}),
                      ...(v !== 'custom_script' ? { custom_script_id: '' } : {}),
                      ...(v !== 'view_page' ? { view_page_script_id: '' } : {}),
                      ...(v !== 'url' ? { target_url: '' } : {})
                    }));
                  }}
                >
                  <option value="">Select action behavior (optional)</option>
                  <option value="url">URL</option>
                  <option value="component">Component</option>
                  <option value="custom_script">Custom Script</option>
                  <option value="view_page">View Page</option>
                  {actionForm.action_behavior &&
                    !isSelectableActionBehavior(actionForm.action_behavior) && (
                      <option value={actionForm.action_behavior}>
                        {labelForActionBehavior(actionForm.action_behavior)} (legacy)
                      </option>
                    )}
                </Form.Select>
                <Form.Text className="text-muted">
                  URL navigation, a registered React component, a <code>custom_script</code> row, or a
                  view page form (<code>script_type = view_page</code>). Component and script pickers
                  require a target object.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {actionForm.action_behavior === 'custom_script' && (
            <Row>
              <Col md={12}>
                {!actionForm.target_object?.trim() ? (
                  <Alert variant="warning" className="py-2 small">
                    Select a <strong>target object</strong> first. Only <code>custom_script</code> rows
                    with that object in <code>target_object</code> appear here.
                  </Alert>
                ) : null}
                <Form.Group className="mb-3">
                  <Form.Label>Script for this object</Form.Label>
                  <Form.Select
                    value={actionForm.custom_script_id}
                    onChange={(e) =>
                      setActionForm((prev) => ({ ...prev, custom_script_id: e.target.value }))
                    }
                    required
                    disabled={!actionForm.target_object?.trim()}
                  >
                    <option value="">
                      {!actionForm.target_object?.trim()
                        ? 'Select target object above…'
                        : customScriptRows.length === 0
                          ? 'No scripts for this object — add rows in custom_script'
                          : 'Select a script…'}
                    </option>
                    {customScriptRows.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.display_label
                          ? `${r.display_label} (${r.script_key})`
                          : r.script_key}
                      </option>
                    ))}
                    {actionForm.custom_script_id &&
                      !customScriptRows.some(
                        (r) => String(r.id) === String(actionForm.custom_script_id)
                      ) && (
                        <option value={actionForm.custom_script_id}>
                          Current selection (reload or check target object)
                        </option>
                      )}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Active scripts from <code>custom_script</code> where{' '}
                    <code>target_object</code> matches the target object above.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}

          {actionForm.action_behavior === 'view_page' && (
            <Row>
              <Col md={12}>
                {!actionForm.target_object?.trim() ? (
                  <Alert variant="warning" className="py-2 small">
                    Select a <strong>target object</strong> first. Lists <code>view_page</code>{' '}
                    scripts scoped to that object or universal (empty <code>target_object</code>).
                  </Alert>
                ) : null}
                <Form.Group className="mb-3">
                  <Form.Label>View page script</Form.Label>
                  <Form.Select
                    value={actionForm.view_page_script_id}
                    onChange={(e) =>
                      setActionForm((prev) => ({
                        ...prev,
                        view_page_script_id: e.target.value,
                        prop_mapping_draft: {},
                      }))
                    }
                    required
                    disabled={!actionForm.target_object?.trim()}
                  >
                    <option value="">
                      {!actionForm.target_object?.trim()
                        ? 'Select target object above…'
                        : webComponentScriptRows.length === 0
                          ? 'No view_page scripts — add custom_script rows with script_type view_page'
                          : 'Select a view page script…'}
                    </option>
                    {webComponentScriptRows.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.display_label
                          ? `${r.display_label} (${r.script_key})`
                          : r.script_key}
                      </option>
                    ))}
                    {actionForm.view_page_script_id &&
                      !webComponentScriptRows.some(
                        (r) => String(r.id) === String(actionForm.view_page_script_id)
                      ) && (
                        <option value={actionForm.view_page_script_id}>
                          Current selection (reload or check target object)
                        </option>
                      )}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    <code>custom_script.script_type = view_page</code>. Submit runs the backend script
                    referenced from the form JSON <code>submit.actionScriptId</code> (in{' '}
                    <code>ui_code</code>). After you pick a script, map parameters below (parent{' '}
                    <code>id</code>, etc.); values are merged into the form submit payload.
                  </Form.Text>
                </Form.Group>
                {String(actionForm.view_page_script_id || '').trim() !== '' ? (
                  <>
                    {viewPageScriptDetailLoading ? (
                      <p className="text-muted small mb-2">Loading script for parameter mapping…</p>
                    ) : null}
                    {!viewPageScriptDetailLoading &&
                    viewPageScriptDetail &&
                    viewPagePropSpecs.length === 0 ? (
                      <Alert variant="light" className="py-2 small mb-2 border">
                        This script has no form <code>fields</code> and no <code>ui_code.propSpec</code>.
                        Add a <code>propSpec</code> array on the custom script to declare parameters (for
                        example the parent record <code>id</code>) that are not part of the visible form.
                      </Alert>
                    ) : null}
                    {renderPropMappingSection(viewPagePropSpecs, 'View page parameter values')}
                  </>
                ) : null}
              </Col>
            </Row>
          )}

          {/* Show Target URL field only when action_behavior is 'url' */}
          {actionForm.action_behavior === 'url' && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Target URL</Form.Label>
                  <Form.Control
                    type="text"
                    value={actionForm.target_url}
                    onChange={(e) => setActionForm({ ...actionForm, target_url: e.target.value })}
                    placeholder="e.g., /records/view"
                    required
                  />
                  <Form.Text className="text-muted">
                    Navigation URL for this action
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                {/* Empty column for layout balance */}
              </Col>
            </Row>
          )}

          {/* Show Component Name field only when action_behavior is 'component' */}
          {actionForm.action_behavior === 'component' && (
            <Row>
              <Col md={12}>
                {!actionForm.target_object?.trim() ? (
                  <Alert variant="warning" className="py-2 small">
                    Select a <strong>target object</strong> first. Only components registered for that
                    object in <code>component_registry</code> appear here.
                  </Alert>
                ) : null}
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Component Name</Form.Label>
                  <Form.Select
                    value={actionForm.component_name}
                    onChange={(e) =>
                      setActionForm((prev) => ({
                        ...prev,
                        component_name: e.target.value,
                        prop_mapping_draft: {},
                      }))
                    }
                    required
                    disabled={!actionForm.target_object?.trim()}
                  >
                    <option value="">
                      {!actionForm.target_object?.trim()
                        ? 'Select target object above…'
                        : registryRows.length === 0
                          ? 'No components for this object — add rows in component_registry'
                          : 'Select a component…'}
                    </option>
                    {registryRows.map((r) => (
                      <option key={r.component_key} value={r.component_key}>
                        {r.display_label
                          ? `${r.display_label} (${r.component_key})`
                          : r.component_key}
                      </option>
                    ))}
                    {actionForm.component_name &&
                      !registryRows.some((r) => r.component_key === actionForm.component_name) && (
                        <option value={actionForm.component_name}>
                          {actionForm.component_name} (current — not in registry for this object)
                        </option>
                      )}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Components registered for this target object only (universal entries with no object
                    are not listed). Choose a target object first.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                {/* Empty column for layout balance */}
              </Col>
            </Row>
          )}

          {actionForm.action_behavior === 'component' &&
            renderPropMappingSection(selectedRegistryRow?.config?.propSpec, 'Component parameter values')}

          <Row>
            <Col md={6}>
              <Form.Check
                type="checkbox"
                label="Active"
                checked={actionForm.is_active}
                onChange={(e) => setActionForm({ ...actionForm, is_active: e.target.checked })}
              />
            </Col>
            {allowStandardActionCheckbox && (
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  label="Standard Action"
                  checked={actionForm.is_standard}
                  onChange={(e) => setActionForm({ ...actionForm, is_standard: e.target.checked })}
                />
              </Col>
            )}
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowActionModal(false)}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveAction}
          disabled={loading || !actionForm.action_label.trim()}
        >
          {loading ? 'Saving...' : (editingAction ? 'Update Action' : 'Create Action')}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return (
    <div className="action-manager">
      <AppToast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => {
          setToast({ show: false, message: '', type: 'success' });
        }}
        delay={5000}
      />

      <div className="action-manager-header">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4>
              <FaBolt className="me-2" />
              {objectName ? `Action Manager - ${objectName}` : 'Action Manager'}
            </h4>
            <p className="text-muted">
              {objectName
                ? `Create and manage actions for ${objectName}`
                : 'Create and manage system actions'
              }
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="primary" onClick={handleCreateAction}>
              <FaPlus className="me-1" />
              Create Action
            </Button>
          </div>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={8}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search detail actions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearchTerm('')}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-center">
                <FaFilter className="me-2 text-muted" />
                <span className="text-muted">
                  {filteredActions.length} detail action{filteredActions.length === 1 ? '' : 's'}
                </span>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading && actions.length === 0 ? (
        <div className="text-center py-4">
          <div className="loading-spinner mb-3"></div>
          <p>Loading actions...</p>
        </div>
      ) : filteredActions.length === 0 ? (
        <Card className="empty-state">
          <Card.Body className="text-center py-5">
            <FaBolt size={48} className="text-muted mb-3" />
            <h6>No Actions Found</h6>
            <p className="text-muted mb-3">
              {actions.length === 0
                ? 'Create your first detail action to get started.'
                : actions.some((a) => isDetailActionType(a.action_type))
                  ? 'No detail actions match your search.'
                  : 'No detail actions for this object yet. Other action types are not shown here.'}
            </p>
            {actions.length === 0 && (
              <Button variant="primary" onClick={handleCreateAction}>
                <FaPlus className="me-1" />
                Create First Action
              </Button>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Header>
            <h5 className="mb-0">Actions List</h5>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>Action Label</th>
                  <th>Action Name</th>
                  <th>Action Type</th>
                  <th>Target Object</th>
                  <th>Action Behavior</th>
                  <th>Action Value</th>
                  <th>Status</th>
                  <th>Standard</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredActions.map(action => (
                  <tr key={action.id}>
                    <td>
                      <strong>{action.action_label}</strong>
                    </td>
                    <td>
                      <code className="text-muted">{action.action_name}</code>
                    </td>
                    <td>
                      {getActionTypeBadge(action.action_type)}
                    </td>
                    <td>
                      {action.target_object ? (
                        <Badge bg="info">{getTargetObjectDisplayLabel(action.target_object)}</Badge>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {action.action_behavior ? (
                        <Badge bg="primary">{labelForActionBehavior(action.action_behavior)}</Badge>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="text-break">
                      {(() => {
                        const b = String(action.action_behavior || '').toLowerCase();
                        if (b === 'url' && action.target_url) {
                          return <span className="text-info">{action.target_url}</span>;
                        }
                        if (b === 'component' && action.component_name) {
                          return <span className="text-primary">{action.component_name}</span>;
                        }
                        if (
                          b === 'custom_script' ||
                          b === 'web_component' ||
                          b === 'ui_schema' ||
                          b === 'view_page'
                        ) {
                          const sk =
                            action.config &&
                            typeof action.config === 'object' &&
                            (action.config.scriptKey || action.config.script_key)
                              ? String(action.config.scriptKey || action.config.script_key)
                              : '';
                          if (sk) {
                            return <code className="small text-secondary">{sk}</code>;
                          }
                        }
                        return <span className="text-muted">—</span>;
                      })()}
                    </td>
                    <td>
                      <Badge bg={action.is_active ? 'success' : 'secondary'}>
                        {action.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      <Badge
                        bg={action.is_standard ? 'warning' : 'info'}
                        text={action.is_standard ? 'dark' : 'white'}
                      >
                        {action.is_standard ? 'Standard' : 'Custom'}
                      </Badge>
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditAction(action)}
                          title="Edit Action"
                          disabled={isStandardActionRowLocked(action)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteAction(action)}
                          title="Delete Action"
                          disabled={isStandardActionRowLocked(action)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {renderActionModal()}

      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setActionToDelete(null);
        }}
        onConfirm={confirmDeleteAction}
        title="Confirm Delete"
        message="Are you sure you want to delete this action?"
        itemName={actionToDelete?.action_label}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default ActionManager;
